import { JSX } from "react";
import { db } from "../../../data/db/db"
import { ArticleCuttingSpecsStd, ArticleSpecs } from "../../../models/articleModel"
import { Material } from "../../../models/materialModel"
import { cuttingsSpeeds, materials as materialList } from "../../../util/materials"
import i18next from "i18next";
import { Trans } from "react-i18next";

/* Erstellt eine formatierte Materialbeschreibung basierend auf Materialeigenschaften. */
/* Wird in Material Autocomplete angezeigt */
export const getMaterialLabel = (option: Material) => {
    let label = ""
    label += "[" + option.gruppe + "] " + option.nummer //zb [P1] 1.0033

    let otherSpecs: { key: string, value: string }[] = []

    if (option.din !== "") {
        otherSpecs.push({ key: "DIN", value: option.din })
    }

    if (option.festigkeit_max !== null && option.festigkeit_min !== null) {
        otherSpecs.push({ key: i18next.t("calculatorForm.formHelpers.Festigkeit"), value: option.festigkeit_min + "-" + option.festigkeit_max })
    } else if (option.festigkeit_min !== null) {
        otherSpecs.push({ key: i18next.t("calculatorForm.formHelpers.Festigkeit"), value: option.festigkeit_min.toString() })
    }

    if (otherSpecs.length > 0) {
        label += " ("
        label += otherSpecs.map(spec => spec.key + ": " + spec.value).join(", ")
        label += ")"
    }

    return label;
}


/* Ruft passende Materialien für einen bestimmte Fräser aus DB ab */
export const getMaterials = async (ident: string, pos: string) => {
    const specFromDB = await db.articlesSpec.where({ ident: ident, pos: pos }).first(); // fräser Specs

    if (specFromDB) { // wenn es mind. einen passenden Fräser gibt
        const length = specFromDB.laenge;

        let matchingSpecs = await getCuttingSpecsStdByIdentAndLength(ident, length);

        if (matchingSpecs) {
            let materialGroups: String[] = []
            matchingSpecs.forEach(spec => {
                let groups = getMaterialGroups(spec)
                materialGroups = materialGroups.concat(groups)
            })
            /* @ts-ignore */
            const materialsFromDB = await db.materials.where("gruppe").anyOf(materialGroups).sortBy("id");
            return materialsFromDB;
        }

    }
    return ([]);
}


const getCuttingSpecsStdByIdentAndLength = async (ident: string, length: string) => {
    const cuttingSpecsStdFromDB = await db.articleCuttingSpecsStd.where({ ident: ident }).toArray()

    /* manche Fräser (zb 2003804C-100) gibt es als zwei Varianten: normal und als spezialset. */
    /* Spezialset geht zb nur für Material S2, während normaler Fräser für fast alle Materialien geht */
    /* Daher muss hier ein Array zurück gegeben werden */
    /* Anschließend muss Materialauswahl berücksichtig werden */
    let matchingSpecs: ArticleCuttingSpecsStd[] = [];
    cuttingSpecsStdFromDB.forEach(entry => {
        const lengths = entry.laengen.split(",")
        if (lengths.includes(length)) {
            matchingSpecs.push(entry)
        }
    })
    return matchingSpecs
}

export const getMaterialGroups = (spec: ArticleCuttingSpecsStd) => {
    let list: string[] = [];

    cuttingsSpeeds.forEach((cuttingsSpeed) => {
        let vcString = "vc" + cuttingsSpeed + "_flag" as keyof typeof spec;
        let vcFlag = spec[vcString]
        if (vcFlag === 1) {
            materialList.forEach((material) => {
                let vcMaterialString = "vc" + cuttingsSpeed + "_" + material as keyof typeof spec
                let vcMaterial = spec[vcMaterialString];
                if (vcMaterial !== 0) {
                    list.indexOf(material.toUpperCase()) === -1 && list.push(material.toUpperCase())
                }
            });
        }
    })

    return list;
}

export const canTrocut = async (specFromDB: undefined | ArticleSpecs) => {
    if (specFromDB && specFromDB.trocut === "1") {
        return true;
    } return false;
}

export const canFullTrack = async (spec: ArticleSpecs | undefined, material: string) => {
    if (spec !== undefined) {
        const matching: null | ArticleCuttingSpecsStd = await getMatchingArticleSpecBasedOnMaterialAndSpec(spec, material)
        if (matching && matching !== null && (matching["ae10_flag"] === 1 || matching["ae1005_flag"] === 1)) {
            return true
        }
    }
    return false;
}

export const canCopy = async (spec: ArticleSpecs | undefined) => {
    // Fräser kann kopieren, wenn dritte Zahl in Ident 1 oder 2 ist
    if (spec !== undefined) {
        if (spec.ident[2] === "1" || spec.ident[2] === "2") {
            return true
        }
        return false;
    }
    return false
}

// gibt gewählte Strategie (zb std-bes) als vollständiges Wort aus
export const mapStrategies = (val: string): string => {
    return i18next.t(`mapStrategies.${val}`, "");
}


const aeFlagsWithFactors: { flag: keyof ArticleCuttingSpecsStd; factor: number }[] = [
    { flag: 'ae005_flag', factor: 0.05 },
    { flag: 'ae01_flag', factor: 0.1 },
    { flag: 'ae02_flag', factor: 0.2 },
    { flag: 'ae03_flag', factor: 0.3 },
    { flag: 'ae06_flag', factor: 0.6 },
    { flag: 'ae10_flag', factor: 1.0 },
];

export const getMaxAEValue = async (article: ArticleSpecs, strategy: string, material: string): Promise<{ value: number; message: JSX.Element }> => {
    if (strategy === "std-bes") {
        const matching: null | ArticleCuttingSpecsStd = await getMatchingArticleSpecBasedOnMaterialAndSpec(article, material)


        if (matching) {

            let lastValidFactor: number | undefined = undefined;

            for (const { flag, factor } of aeFlagsWithFactors) {
                if (matching[flag] === 1) {
                    lastValidFactor = factor;
                } else if (matching[flag] !== 1 && flag === "ae005_flag") {
                    //erst mal weiter machen
                } else {
                    break;
                }
            }

            if (lastValidFactor) {
                const limit = article.d1 * lastValidFactor;

                return {
                    value: limit,
                    message: (
                        <Trans
                            i18nKey="calculatorForm.StdParameterForm.aeLimitMessage"
                            values={{ limit: limit.toLocaleString("de-DE", { maximumFractionDigits: 2 }), factor: lastValidFactor.toLocaleString("de-DE") }}
                            components={{ 1: <sub /> }}
                        />
                    )
                };
            }


        }
        return {
            value: article.d1 * 0.6,
            message: (
                <Trans
                    i18nKey="calculatorForm.StdParameterForm.aeLimitMessage"
                    values={{ limit: (article.d1 * 0.6).toLocaleString("de-DE", { maximumFractionDigits: 2 }), factor: (0.6).toLocaleString("de-DE") }}
                    components={{ 1: <sub /> }}
                />
            )
        };

    }

    return {
        value: article.d1 * 1,
        message: (
            <Trans
                i18nKey="calculatorForm.StdParameterForm.aeLimitMessage1"
                values={{ limit: (article.d1 * 1).toLocaleString("de-DE", { maximumFractionDigits: 2 }) }}
                components={{ 1: <sub /> }}
            />
        )
    };

    //andere Bearbeitungs-Strategien spielen keine Rolle
    //std-copy ist d1*0.03 (wird in ParameterForm.tsx in getInitialAe definiert)
    //std-vsp ist d1*1 (wird in ParameterForm.tsx in getInitialAe definiert)
}

export const getMaxAPValue = (article: ArticleSpecs, strategy: string, ae: number) => {
    let apmax = 1;

    if (strategy === "std-bes") {
        if (ae <= (0.3 * article.d1)) {
            apmax = article.l2;
        } else if (ae > (0.3 * article.d1)) {
            apmax = (article.d1 * 1.5) < article.l2 ? (article.d1 * 1.5) : article.l2;
        } else if (ae > (0.6 * article.d1)) {
            apmax = article.d1 < article.l2 ? article.d1 : article.l2;
        }

        switch (article.ident) {
            case '209 30 04':
            case '209 30 04C':
            case '203 00 00':
            case '203 00 00C':
            case '203 05 00':
            case '203 05 00C':
            case '203 10 00':
            case '203 10 00C':
            case '203 15 00':
            case '203 15 00C':
            case '203 20 00':
            case '203 20 00C':
                apmax = article.l2;
                break;
            case '200 00 02C':
            case '200 00 04C':
                apmax = article.r;
                break;
            case '209 01 04C':
                apmax = article.l2h * 0.8;
                break;
        }


    } else if (strategy === "std-vsp") {
        apmax = article.d1;
    } else if (strategy === "std-ko") {
        apmax = article.l2;
    }
    return ({
        value: apmax,
        message: (
            <Trans
                i18nKey="calculatorForm.StdParameterForm.apLimitMessage"
                values={{ apmax: apmax.toLocaleString("de-DE", { maximumFractionDigits: 2 }) }}
                components={{ 1: <sub /> }}
            />
        )
    })
}

export const mapSpindle = (val: string) => {
    switch (val) {
        case "motor": return i18next.t("calculatorForm.formHelpers.Motorspindel");
        case "getriebe": return i18next.t("translation:calculatorForm.formHelpers.Getriebespindel");
        default: return "";
    }
}

export const getMatchingArticleSpecBasedOnMaterialAndSKU = async (ident: string, pos: string, material: string) => {
    const specFromDB = await db.articlesSpec.where({ ident: ident, pos: pos }).first();
    let resultMatch: ArticleCuttingSpecsStd | null = null;
    if (specFromDB !== undefined) {
        resultMatch = await getMatchingArticleSpecBasedOnMaterialAndSpec(specFromDB, material)
    }
    return resultMatch
}

export const getMatchingArticleSpecBasedOnMaterialAndSpec = async (articleSpec: ArticleSpecs, material: string): Promise<null | ArticleCuttingSpecsStd> => {
    const matching = await getCuttingSpecsStdByIdentAndLength(articleSpec.ident, articleSpec.laenge)
    let resultMatch: ArticleCuttingSpecsStd | null = null;

    matching.forEach(match => {
        let groupList = getMaterialGroups(match);
        if (groupList.includes(material)) {
            resultMatch = match;
        }
    })
    return resultMatch;
}

export const pressureAngleMin = {
    p1: 10.73, p2: 10.21, p3: 9.21, p4: 8.368,
    m1: 7.37, m2: 7.09,
    k1: 10.23, k2: 10.41,
    n1: 11.69, n2: 11.52, n3: 9.58,
    s1: 7.659, s2: 7.09,
    h1: 7.09, h2: 7.09, h3: 7.09
}

export const pressureAngleMax = {
    p1: 36.76, p2: 33.47, p3: 30.64, p4: 15.31,
    m1: 20.56, m2: 15.319,
    k1: 32, k2: 34.31,
    n1: 45.02, n2: 43.53, n3: 30.22,
    s1: 18.36, s2: 15.319,
    h1: 21.27, h2: 16.31, h3: 10.64
}

export const getEingriffswinkelKorrekturFaktor = (d1: number, l2: number, dc: boolean, z: number) => {
    let kf1 = 1;
    let kf2 = 1;

    switch (Math.floor(l2 / d1)) {
        case 0:
        case 1:
            kf1 = 1.52; break;
        case 2:
            kf1 = 1.41; break;
        case 3:
            kf1 = 1.2; break;
        case 4:
            kf1 = 1.1; break;
        default:
            kf1 = 1.0; break;
    }

    if (dc) {
        kf2 = 1.05;
    } else {
        switch (z) {
            case 3:
                kf2 = 1.1; break;
            case 5:
                kf2 = 0.9; break;
            case 7:
                kf2 = 0.8; break;
            default:
                kf2 = 1.0; break
        }
    }

    let kf = kf1 * kf2;

    return kf;
}

export const getPressureAngles = (mat: string) => {
    /* @ts-ignore */
    const min = pressureAngleMin[mat]
    /* @ts-ignore */
    const max = pressureAngleMax[mat]

    return ({ min: min, max: max })
}
